import { WarningFilled } from '@ant-design/icons';
import { Dropdown, Flex } from 'antd';
import cn from 'classnames';

import type { TimeFormat } from '@app/api/Branches/types';

import type {
  AppointmentPreviewField,
  CalendarBreak,
  CalendarEvent,
  CalendarSlotMenu,
  Employee,
} from '@pages/Calendar/data';
import { AppointmentCard } from '@pages/Calendar/ui/AppointmentCard';
import type { EventDragState } from '@pages/Calendar/ui/AppointmentCard/hooks';
import type { IndividualCalendarDay } from '@pages/IndividualCalendar/data';

import { useDayColumn } from '../hooks';

import type { FC } from 'react';

import '../styles/DayColumn.scss';

export interface DayColumnProps {
  day: IndividualCalendarDay;
  dayColumns: Employee[];
  isContentHidden: boolean;
  canCreateRecords: boolean;
  canManageRecords: boolean;
  canOpenRecords: boolean;
  startTime: string;
  endTime: string;
  stepMinutes: number;
  timeFormat?: TimeFormat | null;
  intervalHeightPx: number;
  onSlotClick?: (date: string, time: string, endTime: string) => void;
  onSlotBreakClick?: (date: string, time: string, endTime: string) => void;
  onBreakClick?: (date: string, calendarBreak: CalendarBreak) => void;
  onEventUpdate: (
    eventId: string,
    newStart: string,
    newEnd: string,
    newDate?: string
  ) => void;
  onDragStateChange?: (state: EventDragState | null) => void;
  onRecordClick: (event: CalendarEvent) => void;
  canShowContacts: boolean;
  previewFields: AppointmentPreviewField[];
  activeSlotMenu: CalendarSlotMenu | null;
  onSlotMenuOpen: (slotMenu: CalendarSlotMenu) => void;
  onSlotMenuClose: () => void;
}

export const DayColumn: FC<DayColumnProps> = ({
  day,
  dayColumns,
  isContentHidden,
  canCreateRecords,
  canManageRecords,
  canOpenRecords,
  startTime,
  endTime,
  stepMinutes,
  timeFormat,
  intervalHeightPx,
  onSlotClick,
  onSlotBreakClick,
  onBreakClick,
  onEventUpdate,
  onDragStateChange,
  onRecordClick,
  canShowContacts,
  previewFields,
  activeSlotMenu,
  onSlotMenuOpen,
  onSlotMenuClose,
}) => {
  const {
    dateKey,
    isExecutorNonWorkingDay,
    timeSlots,
    layoutedEvents,
    breakBlocks,
    activeEventId,
    gridHoverHandlers,
    handleBreakClick,
    handleSlotClick,
    slotMenu,
    slotMenuItems,
    closeSlotMenu,
  } = useDayColumn({
    day,
    startTime,
    endTime,
    stepMinutes,
    intervalHeightPx,
    timeFormat,
    onSlotClick,
    onSlotBreakClick,
    onBreakClick,
    activeSlotMenu,
    onSlotMenuOpen,
    onSlotMenuClose,
  });

  return (
    <div
      className={cn('DayColumn', {
        'DayColumn--non-working-day': isExecutorNonWorkingDay,
      })}
      data-testid={`day-column-${dateKey}`}
    >
      {isContentHidden ? (
        <div className="DayColumn__restricted" />
      ) : (
        <div className="DayColumn__grid" {...gridHoverHandlers}>
          {timeSlots.map((slot) => {
            const {
              time,
              label,
              isOccupied,
              isNonWorking,
              isNonWorkingTimeSection,
              isBreak,
              isBeforeBlock,
              isBlockStart,
              isBlockEnd,
              shouldShowNonWorkingTimeLabel,
            } = slot;

            const canCreateInSlot = canCreateRecords && !isOccupied && !isBreak;
            const isDisabled = !canCreateInSlot && !isOccupied && !isBreak;

            return (
              <div
                key={time}
                className={cn('DayColumn__slot', {
                  'DayColumn__slot--occupied': isOccupied,
                  'DayColumn__slot--non-working': isNonWorking,
                  'DayColumn__slot--non-working-time': isNonWorkingTimeSection,
                  'DayColumn__slot--break': isBreak,
                  'DayColumn__slot--before-block': isBeforeBlock,
                  'DayColumn__slot--block-start': isBlockStart,
                  'DayColumn__slot--block-end': isBlockEnd,
                  'DayColumn__slot--disabled': isDisabled,
                })}
                data-testid={`day-slot-${dateKey}-${time}`}
                onClick={
                  isBreak || !canCreateInSlot
                    ? undefined
                    : (event) => handleSlotClick(slot, event)
                }
              >
                {canCreateInSlot && (
                  <div
                    className={cn('DayColumn__slot-hover', {
                      'DayColumn__slot-hover--non-working-time':
                        isNonWorkingTimeSection,
                      'DayColumn__slot-hover--with-warning-label':
                        shouldShowNonWorkingTimeLabel,
                    })}
                  >
                    <Flex gap={4} align="center">
                      <span className="DayColumn__slot-time">{label}</span>
                      <span className="DayColumn__slot-label">Новый сеанс</span>
                    </Flex>
                    {isNonWorkingTimeSection &&
                      shouldShowNonWorkingTimeLabel && (
                        <span className="DayColumn__slot-warning-label">
                          <WarningFilled className="DayColumn__slot-warning-icon" />
                          Нерабочее время
                        </span>
                      )}
                  </div>
                )}
              </div>
            );
          })}

          {/* key по шагу — меню заново встаёт у нового шага. trigger click —
              rc-trigger закрывает попап по клику мимо него только при click;
              якорь с pointer-events: none, клик по шагу доходит до слота. */}
          {slotMenu && (
            <Dropdown
              key={slotMenu.time}
              menu={{ items: slotMenuItems }}
              open
              trigger={['click']}
              placement="bottomLeft"
              onOpenChange={(isOpen) => {
                if (!isOpen) closeSlotMenu();
              }}
            >
              <div
                className="DayColumn__slot-menu-anchor"
                style={{ top: slotMenu.topPx, height: slotMenu.heightPx }}
                data-testid={`day-slot-menu-${dateKey}-${slotMenu.time}`}
              />
            </Dropdown>
          )}

          {breakBlocks.map(({ calendarBreak, topPx, heightPx }) => (
            <div
              key={calendarBreak.id}
              className="DayColumn__break-block"
              style={{ top: topPx, height: heightPx }}
              data-testid={`day-break-${dateKey}-${calendarBreak.id}`}
              onClick={(event) => handleBreakClick(event, calendarBreak)}
            >
              <span className="DayColumn__break-label">Перерыв</span>
            </div>
          ))}

          {layoutedEvents.map(({ event, layout }) => (
            <AppointmentCard
              key={event.id}
              event={event}
              employees={dayColumns}
              layout={layout}
              isActive={activeEventId === event.id}
              calendarStartTime={startTime}
              calendarEndTime={endTime}
              stepMinutes={stepMinutes}
              intervalHeightPx={intervalHeightPx}
              moveEntityLabel="Дата"
              interactionsDisabled={!canManageRecords}
              onEventUpdate={onEventUpdate}
              onDragStateChange={onDragStateChange}
              onRecordClick={canOpenRecords ? onRecordClick : undefined}
              canShowContacts={canShowContacts}
              previewFields={previewFields}
            />
          ))}
        </div>
      )}
    </div>
  );
};
